// @ts-strict-ignore
import React, { useRef, useState } from 'react'
import { IonGrid, IonRow, IonProgressBar, IonCol, IonText } from '@ionic/react'

import { PlayerStatus, AudioPlayerContainer as Container, PlaylistItem } from 'mytours-read-common'

import { getDirMode, isRTL, waitForSomething } from '../../platform'

import { formatPlayPauseLabel } from '../functions'
import { getCurrentSound, onBack15, onForward30, sendAnalyticsEvent } from '../soundController'
import { EventActionsEnum } from '../../logging/analytics'

import FullscreenAudioPlayer from './FullscreenAudioPlayer'
import Button from '../../common/components/Button'

import { containerClose, containerDisappear, containerOpen } from './animations'

import strings from '../../../strings'

import classNames from 'classnames'
import './styles/style.less'
import { useEffect } from 'react'
import usePrevious from 'common_lib/hooks/use-previous'
import { useMemo } from 'react'
import { deleteSectionsQueryParams, getSectionsQueryParams } from 'mobile_web/modules/navigation/queryParams'

interface Props {
	className?: string
	bottomOffset?: number
}

const AudioPlayer: React.FC<Container.Props & Container.Actions & Props> = props => {
	const [showFullScreenPlayer, setShowFullScreenPlayer] = useState<boolean>(false)
	const [animationProcess, setAnimationProcess] = useState<boolean>(false)

	const audioWrapperRef = useRef<HTMLDivElement>()
	const audioWrapperRefNode = audioWrapperRef?.current

	const audioRef = useRef<HTMLIonGridElement>()
	const audioRefNode = audioRef?.current

	const prevCurrentAudio: PlaylistItem = usePrevious(props.currentAudio)

	const bottomOffset = useMemo<number>(() => (props.bottomOffset > -1 ? props.bottomOffset : 56), [props.bottomOffset])

	const showAudioPlayer = useMemo<boolean>(() => {
		const { playerStatus } = props.currentAudio || {}
		if (playerStatus !== PlayerStatus.Pause && playerStatus !== PlayerStatus.Playing) {
			return false
		}

		return true
	}, [props.currentAudio?.playerStatus])

	const isPlaying = useMemo<boolean>(
		() => props.currentAudio?.playerStatus === PlayerStatus.Playing,
		[props.currentAudio?.playerStatus],
	)

	const wrapperStyles =
		showFullScreenPlayer || animationProcess
			? {
					paddingBottom: bottomOffset + 14,
			  }
			: {}

	const toggleFullScreenPlayerModal = (): void => {
		setAnimationProcess(true)
		setShowFullScreenPlayer(!showFullScreenPlayer)
	}

	const onGoFullscreenPlayer = (): void => {
		const { currentAudio, collection } = props

		if (!showFullScreenPlayer) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioOpenPlayer)
		}

		toggleFullScreenPlayerModal()
	}

	const openFullScreenAudio = (): void => {
		const { audioTrack } = props.currentAudio || {}

		if (audioTrack?.coverImages?.length > 0 && (audioTrack.slideshowEnabled || audioTrack.fullScreenEnabled)) {
			onGoFullscreenPlayer()
		}
	}

	useEffect(() => {
		if (props?.currentAudio?.playerStatus === PlayerStatus.Ended) {
			setShowFullScreenPlayer(false)
		}
	}, [props.currentAudio])

	useEffect(
		() => () => {
			setShowFullScreenPlayer(false)
		},
		[],
	)

	useEffect(() => {
		if (prevCurrentAudio?.audioTrack?.audio?.id === props.currentAudio?.audioTrack?.audio?.id) return // audio should be loaded only once

		const { storySectionId, sectionItemId } = getSectionsQueryParams()

		if (!storySectionId || !sectionItemId) return

		const { currentAudio } = props
		const { audioTrack, container } = currentAudio
		const { file, id: audioId } = audioTrack?.audio || {}
		const { id: fileId } = file

		if (sectionItemId !== audioId || storySectionId !== container?.id) return

		waitForSomething(() => getCurrentSound(audioId, container, fileId)).then(() => {
			onGoFullscreenPlayer()
			deleteSectionsQueryParams()
		})
	}, [props.currentAudio, props.container])

	useEffect(() => {
		if (showAudioPlayer) {
			document.body.classList.add('has-audio-player')
		} else {
			document.body.classList.remove('has-audio-player')
		}
	}, [showAudioPlayer])

	useEffect(() => {
		if (showFullScreenPlayer) {
			containerOpen(audioRefNode, bottomOffset)
				.onFinish(() => {
					setAnimationProcess(false)
				})
				.play()
		} else {
			containerClose(audioRefNode, bottomOffset)
				.onFinish(() => {
					setAnimationProcess(false)
				})
				.play()
		}
	}, [showFullScreenPlayer])

	useEffect(() => {
		const { playerStatus } = props.currentAudio || {}

		const isActive =
			prevCurrentAudio?.playerStatus !== playerStatus &&
			playerStatus === PlayerStatus.Playing &&
			prevCurrentAudio?.playerStatus !== PlayerStatus.Pause

		if (isActive) {
			openFullScreenAudio()
			// TODO: remove if it's not needed
			// containerAppear(audioWrapperRefNode)
			// 	.onFinish(() => {
			// 		console.log('containerAppear onFinish')
			// 		openFullScreenAudio()
			// 	})
			// 	.play()
		}
	}, [props.currentAudio])

	const onPlayPause = (): void => {
		const { playerStatus, audioTrack, container } = props.currentAudio || {}
		const { audio } = audioTrack || {}
		const currentSound = getCurrentSound(audio?.id, container, audio?.file?.id)

		if (currentSound && audio) {
			switch (playerStatus) {
				case PlayerStatus.Playing:
					currentSound.pause()
					return
				case PlayerStatus.Pause:
					currentSound.play()
					return
			}
		}
	}

	const onClickBack15 = (): void => {
		const { setCurrentTime, currentAudio, collection } = props

		const { audioTrack, container } = currentAudio || {}
		const { audio } = audioTrack || {}

		sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioSkipBackward)
		onBack15(audio?.id, container, audio?.file?.id, time => {
			setCurrentTime(audio?.id, time, container, audio?.file?.id)
		})
	}

	const onClickForward30 = (): void => {
		const { setCurrentTime, currentAudio, collection } = props

		const { duration, audioTrack, container } = currentAudio || {}
		const { audio } = audioTrack || {}

		sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioSkipForward)
		onForward30(audio?.id, duration, container, audio?.file?.id, time => {
			setCurrentTime(audio?.id, time, container, audio?.file?.id)
		})
	}

	const onStopSound = (): void => {
		const { audioTrack, container } = props.currentAudio || {}
		const { audio } = audioTrack || {}

		const currentSound = getCurrentSound(audio?.id, container, audio?.file?.id)

		containerDisappear(audioWrapperRefNode)
			.onFinish(() => {
				if (currentSound) {
					currentSound.stop()
				}

				setShowFullScreenPlayer(false)
			})
			.play()
	}

	const renderFullScreenAudioPlayer = (): JSX.Element => {
		const { currentAudio, collection } = props

		return (
			<FullscreenAudioPlayer
				collection={collection}
				currentAudio={currentAudio}
				onPlayPause={onPlayPause}
				onBack15={onClickBack15}
				onForward30={onClickForward30}
			/>
		)
	}

	const { currentTime, duration, audioTrack } = props.currentAudio || {}
	const title = audioTrack?.audio?.title || ''

	const renderAudioBarTop = (): JSX.Element => {
		const progressPercent: number | undefined = currentTime / duration

		return (
			<>
				<IonRow>
					<IonProgressBar dir={getDirMode()} className="media-progress-bar" value={progressPercent} />
				</IonRow>
				<IonRow className="title">
					<IonCol>
						<IonText style={{ minHeight: '1rem' }}>{title}</IonText>
					</IonCol>
				</IonRow>
			</>
		)
	}

	const renderAudioBarControls = (isPlaying: boolean): JSX.Element => (
		<IonRow className={`controls ${isRTL() ? 'rtl' : ''}`}>
			<IonCol>
				<Button
					mode="md"
					fill="clear"
					className="time-controls-btn"
					onClick={onClickBack15}
					ariaLabel={strings.accessibility_seek_backward}
				>
					<i className="fak fa-skip-back" aria-label={strings.accessibility_seek_backward} />
				</Button>
			</IonCol>
			<IonCol>
				<Button
					fill="clear"
					onClick={onPlayPause}
					className={classNames(isPlaying ? 'pause-controls-btn' : 'play-controls-btn')}
					ariaLabel={formatPlayPauseLabel(isPlaying, title)}
					// `ariaLabel` won't change unless we remount
					key={formatPlayPauseLabel(isPlaying, title)}
				>
					<i className={`fas fa-${isPlaying ? 'pause' : 'play'}`} />
				</Button>
			</IonCol>
			<IonCol>
				<Button
					mode="md"
					fill="clear"
					className="time-controls-btn"
					onClick={onClickForward30}
					ariaLabel={strings.accessibility_seek_forward}
				>
					<i className="fak fa-skip-forward" aria-label={strings.accessibility_seek_forward} />
				</Button>
			</IonCol>
		</IonRow>
	)

	if (!showAudioPlayer) {
		return null
	}

	return (
		<div className="audio-player-wrapper" ref={audioWrapperRef} dir={getDirMode()}>
			<div className={classNames('audio-player-inner', { fixed: animationProcess || showFullScreenPlayer })}>
				<IonGrid
					className={classNames(props.className, 'audio-player audio-player-bar', { open: showFullScreenPlayer })}
					style={wrapperStyles}
					ref={audioRef}
				>
					<IonRow className="audio-player-row">
						{!showFullScreenPlayer && renderAudioBarTop()}
						<IonRow className="actions">
							<IonCol>
								<Button
									fill="clear"
									onClick={onGoFullscreenPlayer}
									ariaLabel={strings.accessibility_open_fullscreen_player}
								>
									<i className={`far fa-${showFullScreenPlayer ? 'chevron-down' : 'chevron-up'}`} />
								</Button>
							</IonCol>
							{!showFullScreenPlayer && renderAudioBarControls(isPlaying)}
							<IonCol>
								<Button fill="clear" onClick={onStopSound} ariaLabel={strings.accessibility_finish_audio_track}>
									<i className="fal fa-xmark" />
								</Button>
							</IonCol>
						</IonRow>

						{showFullScreenPlayer && !animationProcess && renderFullScreenAudioPlayer()}
					</IonRow>
				</IonGrid>
			</div>
		</div>
	)
}

const AudioPlayerBarComponent = Container.default(AudioPlayer)
export default AudioPlayerBarComponent as any
